import { escapeHtml } from '../mentionChipUtils';
import { CitationMarker, MessageCitation } from './webSearch.types';

// Matches the start of a JSON-like structure: `{` or `[` followed (after optional whitespace) by
// a quote, `{`, or `[`. This lets us detect JSON embedded mid-prose and truncate before it.
const JSON_FRAGMENT_START = /[{[]\s*["{[]/;

// Keeps prose before any JSON fragment. Plain text → as-is. Prose+JSON → prose prefix only.
// Starts with JSON → undefined (popover shows title only).
const cleanCitationString = (raw: string): string | undefined => {
  const trimmed: string = raw.trim();
  const match = JSON_FRAGMENT_START.exec(trimmed);
  const prefix = (match ? trimmed.slice(0, match.index) : trimmed).trim();
  return prefix || undefined;
};

const joinTextChunks = (chunks: Array<{ text?: string }>): string | undefined => {
  const joined = chunks.map((part) => part.text ?? '').join('');
  return joined ? cleanCitationString(joined) : undefined;
};

/**
 * Extracts clean, human-readable text from a raw citation sourceContent value.
 * Accepts a plain string, a partially-parsed array of text chunks, or nullish.
 * A plain string that is itself a JSON-encoded array of text chunks is also handled
 * (the live stream sometimes sends sourceContent double-encoded as a string).
 */
export const extractCitationText = (raw?: string | Array<{ text?: string }> | null): string | undefined => {
  if (!raw) return undefined;
  if (Array.isArray(raw)) return joinTextChunks(raw);
  // raw is a string — check if it's a JSON-encoded array of text chunks before treating as prose
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) return joinTextChunks(parsed as Array<{ text?: string }>);
  } catch {
    // not JSON — fall through to prose path
  }
  return cleanCitationString(raw);
};

const MAX_TITLE_LENGTH = 16;

const truncateTitle = (title: string): string =>
  title.length > MAX_TITLE_LENGTH ? `${title.slice(0, MAX_TITLE_LENGTH)}...` : title;

/**
 * Groups citation markers by their `end` offset.
 * Multiple citations at the same position → one chip with a count badge.
 */
const groupByEnd = (markers: CitationMarker[]): Map<number, CitationMarker[]> => {
  const groups = new Map<number, CitationMarker[]>();
  for (const m of markers) {
    const existing = groups.get(m.end) ?? [];
    groups.set(m.end, [...existing, m]);
  }
  return groups;
};

const buildChipHtml = (group: CitationMarker[]): string => {
  const first = group[0];
  if (!first) return '';
  const title = escapeHtml(truncateTitle(first.title ?? ''));
  const count = group.length - 1;
  const extra = count > 0 ? `<span class="citation-chip-count">+${count}</span>` : '';
  const endAttr = String(first.end);
  return `<span class="citation-chip" data-citation-end="${endAttr}">${title}${extra}</span>`;
};

/**
 * Injects inline citation chip pills into raw text at the specified end offsets.
 * Works on the RAW string (before markdown parse) so character offsets from the BE are valid.
 * Inserts at `end` (after the cited span) working right-to-left to avoid offset drift.
 */
export const injectCitationMarkers = (text: string, markers: CitationMarker[]): string => {
  if (markers.length === 0) return text;

  const valid: CitationMarker[] = markers
    .filter((m) => m.start >= 0 && m.start <= text.length)
    .map((m) => ({ ...m, end: Math.min(m.end, text.length) }));

  const groups = groupByEnd(valid);

  // Sort group end positions descending to insert right-to-left (no offset drift)
  const sortedEnds: number[] = [...groups.keys()].sort((a, b) => b - a);

  let result = text;
  for (const end of sortedEnds) {
    const group = groups.get(end);
    if (!group) continue;
    const chipHtml = buildChipHtml(group);
    result = `${result.slice(0, end)}${chipHtml}${result.slice(end)}`;
  }
  return result;
};

/**
 * Maps message-level citations to CitationMarker for a text part.
 * Since messages have at most one text part, all citations belong to it directly.
 */
export const citationsForPart = (citations: MessageCitation[]): CitationMarker[] =>
  citations.map(({ sourceNumber, url, title, start, end }) => ({
    sourceNumber,
    url,
    title,
    start,
    end,
  }));

/**
 * Given a DOM end offset (from data-citation-end) and the message's citations array,
 * returns the group of citations at that position.
 */
export const getCitationGroupByEnd = (citations: MessageCitation[], end: number): MessageCitation[] =>
  citations.filter((c) => c.end === end);
